import type { LucideIcon } from "lucide-react"
import {
  CheckCircleIcon,
  ChevronDownIcon,
  CircleIcon,
  ClockIcon,
  FilterIcon,
  PlusIcon,
  SearchIcon,
  Trash2Icon,
  XCircleIcon,
} from "lucide-react"
import { useMemo, useState } from "react"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import {
  Link,
  useNavigate,
  useSearchParams,
} from "react-router-dom"

import { Button } from "@/components/ui/button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
} from "@/components/ui/form"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { FRONTEND_ROUTES } from "@/constants"
import { SmartRoutingStatusEnum } from "@/enums/smart-routing"
import { cn } from "@/lib/utils"

interface FilterSmartRoutingFormValues {
  status: SmartRoutingStatusEnum | "ALL"
}

interface StatusOption {
  value: SmartRoutingStatusEnum | "ALL"
  label: string
  icon: LucideIcon
}

export const SmartRoutingFilterForm = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [isFilterOpen, setIsFilterOpen] = useState(false)

  const statusOptions: StatusOption[] = [
    {
      value: "ALL",
      label: t("smartRouting.filters.allStatus"),
      icon: CircleIcon,
    },
    {
      value: SmartRoutingStatusEnum.ACTIVE,
      label: t("smartRouting.status.ACTIVE"),
      icon: CheckCircleIcon,
    },
    {
      value: SmartRoutingStatusEnum.PENDING,
      label: t("smartRouting.status.PENDING"),
      icon: ClockIcon,
    },
    {
      value: SmartRoutingStatusEnum.INACTIVE,
      label: t("smartRouting.status.INACTIVE"),
      icon: XCircleIcon,
    },
  ]

  const currentStatus =
    (searchParams.get("status") as
      | SmartRoutingStatusEnum
      | "ALL") ?? "ALL"

  const [statusSelected, setStatusSelected] =
    useState<StatusOption>(
      statusOptions.find(
        (opt) => opt.value === currentStatus
      ) ?? statusOptions[0]
    )

  const form = useForm<FilterSmartRoutingFormValues>({
    defaultValues: {
      status: currentStatus,
    },
  })

  const hasActiveFilter = useMemo(() => {
    return (
      searchParams.get("status") !== "ALL" &&
      Boolean(searchParams.get("status"))
    )
  }, [searchParams])

  const handleSubmit = form.handleSubmit((values) => {
    const params = new URLSearchParams()
    if (values.status && values.status !== "ALL")
      params.set("status", values.status)
    navigate(`?${params.toString()}`)
  })

  const handleReset = () => {
    form.reset({ status: "ALL" })
    setStatusSelected(statusOptions[0])
    navigate("?")
  }

  return (
    <div className="@container">
      {/* Mobile filter toggle */}
      <button
        type="button"
        onClick={() => setIsFilterOpen((prev) => !prev)}
        className="mb-4 flex w-full items-center justify-between rounded-md border bg-background px-4 py-3 text-sm font-medium md:hidden"
      >
        <span className="flex items-center gap-2">
          <FilterIcon size={16} />
          {t("common.filters")}
          {hasActiveFilter && (
            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary text-xs text-primary-foreground">
              !
            </span>
          )}
        </span>
        <ChevronDownIcon
          size={16}
          className={cn(
            "transition-transform duration-200",
            isFilterOpen && "rotate-180"
          )}
        />
      </button>

      <div
        className={cn(
          "md:block",
          isFilterOpen ? "block" : "hidden"
        )}
      >
        <Form {...form}>
          <form
            onSubmit={handleSubmit}
            className="grid gap-4 @4xl:grid-cols-[1fr,auto]"
          >
            <div className="grid gap-4 @xl:grid-cols-2 @3xl:grid-cols-3">
              <FormField
                control={form.control}
                name="status"
                render={({ field }) => (
                  <FormItem>
                    <Popover>
                      <FormControl>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className="h-10 w-full shrink-0 justify-start"
                          >
                            <statusSelected.icon className="mr-2 h-4 w-4" />
                            {statusSelected.label}
                          </Button>
                        </PopoverTrigger>
                      </FormControl>
                      <PopoverContent
                        className="w-[var(--radix-popover-trigger-width)] p-0"
                        align="start"
                      >
                        <Command>
                          <CommandInput
                            placeholder={t(
                              "smartRouting.filters.searchStatus"
                            )}
                          />
                          <CommandList>
                            <CommandEmpty>
                              {t(
                                "smartRouting.filters.noStatusFound"
                              )}
                            </CommandEmpty>
                            <CommandGroup>
                              {statusOptions.map(
                                (option) => (
                                  <CommandItem
                                    key={option.value}
                                    value={option.label}
                                    onSelect={() => {
                                      form.setValue(
                                        "status",
                                        option.value
                                      )
                                      setStatusSelected(
                                        option
                                      )
                                    }}
                                  >
                                    <option.icon
                                      className={cn(
                                        "mr-2 h-4 w-4",
                                        option.value ===
                                          field.value
                                          ? "opacity-100"
                                          : "opacity-40"
                                      )}
                                    />
                                    <span>
                                      {option.label}
                                    </span>
                                  </CommandItem>
                                )
                              )}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </FormItem>
                )}
              />
            </div>

            <div className="flex w-full flex-col justify-start gap-4 @xl:flex-row-reverse @4xl:flex-col">
              <div className="flex gap-2">
                <Button asChild variant="outline">
                  <Link
                    to={
                      FRONTEND_ROUTES
                        .SMART_ROUTING_CONFIGURATION_CREATE
                        .href
                    }
                  >
                    <PlusIcon size={18} className="mr-2" />
                    {t(
                      "pages.smartRouting.newConfiguration"
                    )}
                  </Link>
                </Button>
                <Button className="btn-whitelabel">
                  <SearchIcon size={18} className="mr-2" />
                  {t("common.search")}
                </Button>
              </div>
              {hasActiveFilter && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleReset}
                >
                  <Trash2Icon size={18} className="mr-2" />
                  {t("common.clear")}
                </Button>
              )}
            </div>
          </form>
        </Form>
      </div>
    </div>
  )
}
